import { Badge, Button, Card, CardHeader, EmptyState } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createImport, listImports } from "../../api/imports";
import { listBranches } from "../../api/settings";
import { useServices } from "../../app/services";
import { Field, Load, Page, TableSkeleton, fmtDateTime, mapError } from "../settings/kit";
import { section } from "../settings/sections";
import s from "../settings/settings.module.css";
import { useImportScope } from "./shared";
import { t } from "./strings";

const TONE = { done: "ok", failed: "crit", cancelled: "neutral", cutting_over: "info", approved: "info", dry_run_failed: "crit" } as const;

/** `/settings/import`: the imports so far, and the form that starts one. */
export function ImportsPage() {
  const { client } = useServices();
  const sc = useImportScope();
  const nav = useNavigate();
  const canEdit = sc.ctx.has("settings.imports_edit");
  const list = useQuery({ queryKey: sc.listKey, queryFn: () => listImports(client), retry: false });
  const [form, setForm] = useState(false);
  const [label, setLabel] = useState("");
  const [branchId, setBranchId] = useState("");
  const [onError, setOnError] = useState<"skip_rows" | "abort">("skip_rows");
  // The picker lists branches the caller may see; without that lever the caller's own branch is the only choice.
  const branches = useQuery({ queryKey: sc.key("imports", "branches"), queryFn: () => listBranches(client), enabled: form, retry: false });
  const own = sc.ctx.me?.branch ?? null;
  const options = (branches.data ?? []).filter((b) => b.archivedAt === null).map((b) => ({ id: b.id, name: b.name }));
  const choices = options.length > 0 ? options : own ? [{ id: own.id, name: own.name }] : [];
  const chosen = branchId || own?.id || choices[0]?.id || "";
  const create = useMutation({
    mutationFn: () => createImport(client, { label: label.trim(), defaultBranchId: chosen, onError }),
    onSuccess: (job) => { sc.putJob(job); nav(`/settings/import/${job.id}`); },
  });
  const mapped = create.error ? mapError(create.error, ["label", "defaultBranchId"]) : null;

  return (
    <Page title={t.title} description={t.intro} allow={section("import")}
      actions={canEdit ? <Button variant="primary" onClick={() => setForm(true)}>{t.start}</Button> : undefined}>
      {form && canEdit ? (
        <Card aria-label={t.newTitle}>
          <CardHeader title={t.newTitle} />
          <form className={s.fields} onSubmit={(e) => { e.preventDefault(); if (label.trim() && chosen) create.mutate(); }}>
            <Field label={t.label} hint={t.labelHint} error={mapped?.fields.label} htmlFor="imp-label" full>
              <input id="imp-label" className={s.input} value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} aria-invalid={!!mapped?.fields.label} />
            </Field>
            <Field label={t.branch} hint={t.branchHint} error={mapped?.fields.defaultBranchId} htmlFor="imp-branch">
              <select id="imp-branch" className={s.select} value={chosen} onChange={(e) => setBranchId(e.target.value)}>
                {choices.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label={t.onError} htmlFor="imp-onerror">
              <select id="imp-onerror" className={s.select} value={onError} onChange={(e) => setOnError(e.target.value as "skip_rows" | "abort")}>
                <option value="skip_rows">{t.skipRows}</option>
                <option value="abort">{t.abort}</option>
              </select>
            </Field>
            {mapped?.form ? <div className={`${s.formErr} ${s.full}`} role="alert" style={{ margin: 0 }}>{mapped.form}</div> : null}
            <div className={`${s.acts} ${s.full}`}>
              <Button variant="primary" type="submit" busy={create.isPending} busyLabel="Creating…" disabled={!label.trim() || !chosen}>{t.create}</Button>
              <Button variant="plain" onClick={() => setForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      ) : null}
      <Card aria-label={t.listTitle}>
        <CardHeader title={t.listTitle} />
        <Load query={list} what="imports" skeleton={<TableSkeleton rows={3} />}>
          {(jobs) => jobs.length === 0 ? (
            <EmptyState compact icon="receipt" title={t.empty.title} description={t.empty.body} action={canEdit ? <Button variant="primary" onClick={() => setForm(true)}>{t.start}</Button> : undefined} />
          ) : (
            <div role="list">
              {jobs.map((j) => (
                <div key={j.id} role="listitem" className={s.li}>
                  <span><Link to={`/settings/import/${j.id}`}><b className={s.name}>{j.label}</b></Link><small className={s.sub}>{fmtDateTime(j.createdAt, sc.ctx.timeZone, sc.ctx.locale)} · {j.files.length === 0 ? "no sheets" : `${j.files.length} sheet(s)`}</small></span>
                  <span className={s.acts}><Badge tone={TONE[j.status as keyof typeof TONE] ?? "warn"}>{j.status.replace(/_/g, " ")}</Badge><Button as={Link} to={`/settings/import/${j.id}`} size="sm">{t.resume}</Button></span>
                </div>
              ))}
            </div>
          )}
        </Load>
      </Card>
    </Page>
  );
}
